'use client';

import { useMemo, useRef, useEffect } from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import {
  Paper,
  Stack,
  Typography,
  Fade,
  Box,
  CircularProgress,
} from '@mui/material';
import { User } from '../../auth/authState';

import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import 'ag-grid-enterprise';

import ActionsRenderer from '../../components/cell-renderers/ActionsRenderer';

import {
  DEFAULT_COL_DEF,
  DEFAULT_LOCALIZATION,
  PAGE_SIZE_SELECTOR,
} from '../../utils/agGrid';

import userHasPermission from '../../utils/functions/userHasPermission';
import { formatCurrency } from '../../utils/functions/formatCurrency';

import {
  RolePermission,
  OrderState,
  OrderDto,
  formatUserNameOrId,
  getUserSearchText,
} from 'desk-common';

import {
  ORDER_STATE_CONFIG,
  StateRenderer,
} from '../../components/orders/OrderStateChip';

interface Props {
  rowData: OrderDto[];
  isLoading: boolean;
  user: User | null;
  onEdit: (order: OrderDto) => void;
  onView: (order: OrderDto) => void;
  filterState?: OrderState | null;
}

const baseColumns: ColDef<OrderDto>[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 120,
    pinned: 'left',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'userCreated',
    headerName: 'Solicitante',
    flex: 1,
    minWidth: 200,
    filter: 'agTextColumnFilter',
    valueGetter: (params) =>
      formatUserNameOrId(params.data?.userCreated, params.data?.userCreatedId),
    filterValueGetter: (params) =>
      getUserSearchText(params.data?.userCreated, params.data?.userCreatedId),
  },
  {
    colId: 'costCenterApprover',
    headerName: 'Aprobador',
    flex: 1,
    minWidth: 200,
    filter: 'agTextColumnFilter',
    valueGetter: (params) =>
      formatUserNameOrId(
        params.data?.costCenterApprover,
        params.data?.costCenterApproverId ?? params.data?.costCenter?.approverId
      ),
    filterValueGetter: (params) =>
      getUserSearchText(
        params.data?.costCenterApprover,
        params.data?.costCenterApproverId ?? params.data?.costCenter?.approverId
      ),
  },
  {
    field: 'costCenterName',
    headerName: 'Centro de costo',
    flex: 1,
    minWidth: 200,
    filter: 'agTextColumnFilter',
    valueGetter: (params) => params.data?.costCenter?.name || '',
  },
  {
    field: 'state',
    headerName: 'Estado',
    flex: 1,
    minWidth: 170,
    filter: 'agSetColumnFilter',
    cellRenderer: StateRenderer,
    filterParams: {
      valueFormatter: (params: { value: OrderState }) => {
        return (
          ORDER_STATE_CONFIG[params.value as keyof typeof ORDER_STATE_CONFIG]
            ?.label ?? params.value
        );
      },
    },
  },
  {
    field: 'orderTotal',
    headerName: 'Total',
    maxWidth: 160,
    flex: 1,
    valueGetter: (d) => {
      return d.data?.orderTotal ? Number(d.data?.orderTotal) : null;
    },
    valueFormatter: ({ value }) => formatCurrency(value),
    filter: 'agNumberColumnFilter',
    suppressFloatingFilterButton: true,
  },
  {
    field: 'createdAt',
    headerName: 'Fecha creación',
    minWidth: 180,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: (filterDate: Date, cellValue: string) => {
        if (!cellValue) {
          return -1;
        }
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
        filterDate.setHours(0, 0, 0, 0);
        if (cellDate < filterDate) {
          return -1;
        }
        if (cellDate > filterDate) {
          return 1;
        }
        return 0;
      },
    },
    valueFormatter: ({ value }) => new Date(value).toLocaleString(),
  },
];

const orderPermissions: Partial<Record<OrderState, { canEdit: boolean }>> = {
  [OrderState.BORRADOR]: { canEdit: true },
  [OrderState.PENDIENTE_APROBACION_CENTRO_COSTO]: { canEdit: true },
  [OrderState.ANULADO_POR_CENTRO_COSTO]: { canEdit: false },
  [OrderState.PENDIENTE_APROBACION_AREA_COMPRAS]: { canEdit: true },
  [OrderState.APROBADO]: { canEdit: false },
  [OrderState.ANULADO_POR_AREA_COMPRAS]: { canEdit: false },
};

export default function OrdersTable({
  rowData,
  isLoading,
  user,
  onEdit,
  onView,
  filterState,
}: Props) {
  const gridRef = useRef<AgGridReact<OrderDto>>(null);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      if (filterState) {
        gridRef.current.api
          .setColumnFilterModel('state', { values: [String(filterState)] })
          .then(() => {
            gridRef.current?.api.onFilterChanged();
          });
      } else {
        gridRef.current.api.setColumnFilterModel('state', null).then(() => {
          gridRef.current?.api.onFilterChanged();
        });
      }
    }
  }, [filterState]);

  const columns = useMemo<ColDef<OrderDto>[]>(
    () => [
      {
        colId: 'actions',
        headerName: 'Acciones',
        width: 120,
        pinned: 'right',
        sortable: false,
        resizable: false,
        filter: false,
        cellRenderer: ActionsRenderer,
        cellRendererParams: (params: { data: OrderDto }) => {
          const { data } = params;
          const permissions = orderPermissions[data.state];

          return {
            actions: [
              {
                icon: <VisibilityOutlinedIcon />,
                tooltip: 'Ver pedido',
                disabled:
                  !user ||
                  (!userHasPermission(user, RolePermission.readOrders) &&
                    !userHasPermission(user, RolePermission.readOwnOrders)),
                onClick: () => onView(data),
              },
              {
                icon: <EditIcon />,
                tooltip: 'Editar pedido',
                disabled:
                  !user ||
                  !(permissions?.canEdit ?? false) ||
                  (!userHasPermission(user, RolePermission.readOrders) &&
                    !userHasPermission(user, RolePermission.readOwnOrders)),
                onClick: () => onEdit(data),
              },
            ],
          };
        },
      },
      ...baseColumns,
    ],
    [user, onEdit, onView]
  );

  return (
    <Paper className="ag-theme-quartz" sx={{ position: 'relative' }}>
      {isLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="500px"
        >
          <CircularProgress />
        </Box>
      )}
      {!isLoading && rowData.length === 0 && (
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          spacing={2}
        >
          <Typography variant="h6">No hay pedidos registrados</Typography>
          <Typography variant="body2" color="text.secondary">
            Crea el primer pedido para comenzar
          </Typography>
        </Stack>
      )}
      {!isLoading && rowData.length > 0 && (
        <Fade in timeout={250}>
          <div style={{ height: '100%' }}>
            <AgGridReact<OrderDto>
              ref={gridRef}
              rowData={rowData}
              columnDefs={columns}
              pagination
              paginationPageSize={10}
              paginationPageSizeSelector={PAGE_SIZE_SELECTOR}
              defaultColDef={
                {
                  ...DEFAULT_COL_DEF,
                  filter: true,
                  floatingFilter: true,
                } as ColDef<OrderDto>
              }
              localeText={DEFAULT_LOCALIZATION}
              animateRows
              suppressMultiSort
              rowHeight={56}
              headerHeight={56}
              suppressCellFocus
            />
          </div>
        </Fade>
      )}
    </Paper>
  );
}
